/**
 * Created by hsmasud on 2/11/2019.
 */
import { HttpClient } from '@angular/common/http';
import { select, Store } from '@ngrx/store';
import { CookieService } from 'ngx-cookie-service';
import { environment } from '../../../../src/environments/environment';
import { Observable, zip } from 'rxjs';
import { SocketClusterService } from '../../../../src/app/core/services/socket-cluster.service';
import { ParticleCacheService } from '../../../../src/app/core/services/particle-cache.service';
import { Configuration } from '@shared/models/configuration';
import { dynamicSettingsToUse } from '@shared/helpers/served-dynamic-settings';
import { getAccessToken, getClaims, isAuthenticated } from '@shared/store/selectors/security.selector';
import {
  GetAccessTokenAction,
  GetAccessTokenSuccessAction,   GetCompanyBraningProfileSuccessAction, GetUserProfileSuccessAction, RunDemoWorkflowAction,
  SetDemoAction,
  SetSupportAccessSessionAction,
  SetTempAccessTokenAction
} from '@shared/store/actions/auth.actions';
import { getCurrentProject, ProjectState } from '@features/projects/store/reducers/projects.reducer';
import { ParticleCacheModel } from '@shared/models/particle-cache.model';
import { CreateDefaultProjectAction } from '@features/projects/store/actions/projects.actions';
import { SecurityState } from '@shared/store/reducers/security.reducer';
import { AccessTokenModel } from '@shared/models/access-token.model';
import { CreateDefaultProjectCommand } from '@features/projects/commands/projects.commands';
import { GetToastsAction } from '@shared/store/toast/toast-actions';
import { AuthorizationType } from '@shared/models/AuthorizationType';
import { UrlUtility } from '@shared/utility/url-utility';
import { take } from 'rxjs/operators';
import { PlatformCommand } from '@shared/commands/command-base';
import { Router } from "@angular/router";
import { appRoutesAdminpanel } from "../../../../src/app/app.routes.adminpanel";
import { appRoutes } from "../../../../src/app/app.routes";
import {TellMe} from "@shared/models/TellMe";
import { DemoProvisioningService } from '@shared/services/demo-provisioning.service';
import {
  addressAfterSignIn, buildAuthorizeState, buildAuthorizeUrl, callbackUri, currentReturnPath, nextSignInStep
} from '../../../../src/app/core/services/oauth-return-path';
import {
  captureSqaRun, isSqaRunMode, isSqaRunSignedIn, markSqaRunSignedIn, sqaRunSignInHints
} from '../../../../src/app/core/services/sqa-run-mode';
import { installSqaRunEvidence } from '../../../../src/app/core/services/sqa-run-evidence.service';
import { endLostSessionAtBoot } from '../../../../src/app/core/services/session-cookie';

export class ApplicationInitializer {
  private static isAuthenticated$: any;
  private static redirectURLSetOnce = false;
  private static currentProject$: Observable<any>;
  private static demoConfig: any;
  private static getUrlParam(name): string {
    const results = new RegExp('[\?&]' + name + '=([^&#]*)').exec(window.location.search);
    if (results == null) {
      return null;
    } else {
      return decodeURI(results[1]) || '0';
    }
  }

  private static doAuthentication(securityStore, cookieService, router: Router) {
    const self = this;
    // url params throw exceptions in IE  const urlParams = new URLSearchParams(window.location.search);
    const authentication_code = this.getUrlParam('code');
    const crsf_state = this.getUrlParam('state');
    const redirectFromDashboard = this.getUrlParam('rlogin');
    // TSP-E-157-22: `?sqa-run=<key>` is read here, before anything can start Userflow, and kept for the browser
    // session. It survives a sign-in the webclient starts itself: oauth-return-path keeps every param but these three.
    captureSqaRun();
    // TSP-E-177-02: an SQA run's tab keeps its console and page errors from here on, for the local agent's bundle.
    installSqaRunEvidence();
    let cookiedAccessToken = new AccessTokenModel();
    let cookieExpiryTime;
    // SHOW-E-13-01: authorize and exchange the code with the one registered callback; the requested page
    // rides in `state` (see oauth-return-path.ts). `rlogin` is stripped from that page like `code`/`state`.
    Configuration.setReDirectURI(callbackUri());
    // TSP-E-157-24: a `?code=` is exchanged once, before any cookied or dummy token (nextSignInStep).
    let landAfterExchange = !!authentication_code;
    let codeSent = false;
    if (redirectFromDashboard === 'fr' || authentication_code) {
      cookiedAccessToken = null;
    } else {
      cookiedAccessToken.access_token = cookieService.get(Configuration.CookiePrefix + 'Orbitax-Access_token');
      cookiedAccessToken.expires_in = cookieService.get(Configuration.CookiePrefix + 'Orbitax-Expires_in') ?
        parseInt(cookieService.get(Configuration.CookiePrefix + 'Orbitax-Expires_in'), 10) : 0;
      cookiedAccessToken.refresh_token = cookieService.get(Configuration.CookiePrefix + 'Orbitax-Refresh_token');
      cookiedAccessToken.token_type = cookieService.get(Configuration.CookiePrefix + 'Orbitax-Token_type');
      cookieExpiryTime = cookieService.get(Configuration.CookiePrefix + 'Orbitax-Cookie_expiry');
    }

    if (cookieExpiryTime) {
      const expiryTime = new Date(cookieExpiryTime);
      const currentTime = new Date();
      if (expiryTime < currentTime) {
        cookiedAccessToken = null;
      }
    }
    if (cookiedAccessToken && cookiedAccessToken.access_token === '') {
      cookiedAccessToken = null;
    }

    this.isAuthenticated$ = securityStore.select(isAuthenticated);
    securityStore.select(getAccessToken).subscribe(accessToken => {
      // console.log('ApplicationInitializer - hit ');
      if (accessToken === undefined) { return; } // when refreshing token

      (window as any).orbitaxPlatformGtag('set', { 'user_id': cookieService.get('BrowserCode') });
      // TSP-E-157-26: an SQA run signs in through the standard authorize only — never the dummy token, never a
      // session that was open for another sign-in.
      const sqaRun = isSqaRunMode();
      const step = nextSignInStep({
        hasAccessToken: !!accessToken, code: authentication_code, codeSent,
        hasCookiedToken: !!cookiedAccessToken, dummyTokenAllowed: Configuration.AllowDummyToken && !environment.production,
        sqaRun, sqaRunSignedIn: sqaRun && isSqaRunSignedIn()
      });
      if (step === 'land') {
        if (landAfterExchange) {
          landAfterExchange = false;
          if (sqaRun) {
            markSqaRunSignedIn();
          }
          ApplicationInitializer.landOnReturnPath(addressAfterSignIn(crsf_state), router);
        }
      } else if (step === 'exchange') {
        codeSent = true;
        securityStore.dispatch(new GetAccessTokenAction(authentication_code, crsf_state));
      } else if (step === 'cookie') {
        securityStore.dispatch(new GetAccessTokenSuccessAction(cookiedAccessToken));
      } else if (step === 'dummy') {
        const tok = new AccessTokenModel();
        const roleToken = window.localStorage.getItem('MarketPlaceToken');
        switch (roleToken) {
          case 'Author':
            tok.access_token = Configuration.getConfig('MarketPlaceAuthor');
            break;
          case 'Owner':
            tok.access_token = Configuration.getConfig('MarketPlaceOwner');
            break;
          case 'FirmClient':
            tok.access_token = Configuration.getConfig('FirmClient');
            break;
          case 'FirmAdmin':
            tok.access_token = Configuration.getConfig('FirmAdmin');
            break;
          case 'FirmConsultant':
            tok.access_token = Configuration.getConfig('FirmConsultant');
            break;
          case 'FirmManager':
            tok.access_token = Configuration.getConfig('FirmManager');
            break;
          case 'FirmMangerConsultant':
            tok.access_token = Configuration.getConfig('FirmMangerConsultant');
            break;
          case 'FirmWorkspace':
            tok.access_token = Configuration.getConfig('FirmWorkspace');
            break;
          case 'GeneratedToken':
            tok.access_token = window.localStorage.getItem('GeneratedToken');
            break;
          case 'Stakeholder':
            tok.access_token = Configuration.getConfig('Stakeholder');
            break;
          default:
            tok.access_token = Configuration.DummyToken;
            break;
        }
        Configuration.UsingDummyToken = true;
        securityStore.dispatch(new GetAccessTokenSuccessAction(tok));
      } else if (step === 'authorize') {
        // ITP2-14341: this tab's session cookie was deleted, so the session is over - end it (the IdP's too) rather
        // than let the authorize sign the user straight back in. `rlogin` asks for a fresh sign-in on purpose.
        if (!redirectFromDashboard && endLostSessionAtBoot(name => cookieService.get(name))) {
          return;
        }
        // console.log('OAuthAuthorizationAPILink', Configuration.OAuthAuthorizationAPILink);
        const state = buildAuthorizeState(currentReturnPath());
        // TSP-E-157-28: an SQA run's authorize carries the sign-in hints its landing address brought (pre-fill only).
        window.location.href = buildAuthorizeUrl(Configuration.OAuthAuthorizationAPILink, callbackUri(), state, sqaRun,
          sqaRun ? sqaRunSignInHints() : {});
      }
      // 'wait': the code is out for exchange; a refused one leaves the app unauthorized (sign-in failure).
    });

    securityStore.select(getClaims).pipe(take(1)).subscribe(claims => {
      // console.log('[MagicLinkOnlyUrl] Check is started.');
      if (claims['MagicLinkOnlyRedirectUrl']) {
        ApplicationInitializer.handleMagicLinkOnly(claims);
      }
      // console.log('[MagicLinkOnlyUrl] Check is ended.');
    });
  }

  /**
   * After the code exchange, put the page the visitor asked for in the address bar. The router has not made
   * its initial navigation yet (it waits for this initializer), so replacing the history entry is enough —
   * the initial navigation then routes to it. If the router is already up, navigate instead.
   */
  private static landOnReturnPath(returnPath: string, router: Router) {
    const loc = window.location;
    if (returnPath === loc.pathname + loc.search + loc.hash) {
      return;
    }
    if (router && router.navigated) {
      router.navigateByUrl(returnPath, { replaceUrl: true });
    } else {
      window.history.replaceState(window.history.state, '', returnPath);
    }
  }

  private static handleMagicLinkOnly(claims: any) {
    const magicLinkOnlyUrl = new URL(claims['MagicLinkOnlyRedirectUrl'])?.href;
    // console.log(`[MagicLinkOnlyUrl] : ${magicLinkOnlyUrl}`);

    const url = new URL(window?.location?.href);

    const currentUrl = url?.href;
    // console.log(`[CurrentUrl] : ${currentUrl}`);

    const origin = url.origin;
    // console.log(`[Origin]: ${origin}`);

    const accessDeniedUrl = new URL('/access-denied', origin)?.href;
    // console.log(`[accessDeniedUrl] : ${accessDeniedUrl}`);

    if (currentUrl && magicLinkOnlyUrl && !UrlUtility.isSameUrl(currentUrl, magicLinkOnlyUrl) && !UrlUtility.isSameUrl(currentUrl, accessDeniedUrl)) {
      // console.log(`[AccessDenied] CurrentUrl: ${currentUrl} is not same as MagicLinkOnlyUrl: ${magicLinkOnlyUrl}`);
      window.location.href = accessDeniedUrl;
    }
  }

  static resolveConfig(httpClient: HttpClient, env: any, projectStore: Store<ProjectState>,
    socketClusterService: SocketClusterService, particleCacheService: ParticleCacheService,
    securityStore: Store<SecurityState> = null, router: Router): any {

    const cacheConfig = particleCacheService.getItem('clientConfig');

    let configCacheVersion = null;
    if (cacheConfig) {
      configCacheVersion = cacheConfig.version;
    }

    const configUrl = `${Configuration.SecurityApi}Configuration/AllApplications?appId=${Configuration.ApplicationId}${configCacheVersion && Configuration.IsProduction ? '&clientConfigVersion=' + configCacheVersion : ''}`;

    return httpClient.get(configUrl)
      .toPromise()
      .then(data => {
        // console.log(`Config from API: `, data);
        if (data) {
          let clientConfig = data['config'];
          const helpModels = data['helpModels'];
          const onBoarding = data['onBoarding'];
          const hasUserConfirmedOnBoarding = {
            type: 'hasUserConfirmedOnBoarding',
            value: data['hasUserConfirmedOnBoarding'],
            version: clientConfig.ClientConfigVersion
          };
          if(clientConfig){
            TellMe.setBrowserCode(clientConfig['BrowserCode']);
          }
          if (data['showDownTimeMessage']) {
            const downTimeModel = {
              downTimeStart: data['downtimeStart'],
              downTimeEnd: data['downtimeEnd'],
              copyRightMessage: data['downtimeCopyRightMessage'],
              downTimeTitleMessage: data['downTimeTitleMessage'],
              downTimeContentMessage: data['downTimeContentMessage'],
              downTimeFooterMessage: data['downTimeFooterMessage']
            }
            // particleCacheService.saveItem('downTimeMessageModel', downTimeCacheModel);
            clientConfig['downTimeMessageModel'] = downTimeModel;
          }
          if (data['sessionId']) {
            clientConfig['sessionId'] = data['sessionId'];
          }
          if (data['OnBoardingTimeoutTime']) {
            clientConfig['OnBoardingTimeoutTime'] = data['OnBoardingTimeoutTime'];
          }
          const isNewUser = {
            type: 'isNewUser',
            value: data['isNewUser'],
            version: clientConfig.ClientConfigVersion
          };

          if (!clientConfig) {
            clientConfig = cacheConfig.value;
          } else {
            const config: ParticleCacheModel = {
              type: 'clientConfig',
              value: clientConfig,
              version: clientConfig.ClientConfigVersion
            };
            particleCacheService.saveItem('clientConfig', config);
          }
          particleCacheService.saveItem('helpModels', helpModels);
          particleCacheService.saveItem('onBoarding', onBoarding);
          particleCacheService.saveItem('hasUserConfirmedOnBoarding', hasUserConfirmedOnBoarding);
          particleCacheService.saveItem('isNewUser', isNewUser);
          clientConfig.dynamicsettings = dynamicSettingsToUse(Configuration.IsProduction, clientConfig.dynamicsettings, env.dynamicsettings);

          Configuration.setConfig(clientConfig);

          switch (Configuration.getConfig("AppRoute")) {
            case "adminpanel":
              // router.resetConfig(appRoutesAdminpanel);
              break;
            default:
              //router.resetConfig(appRoutes);
              break;
          }
          Configuration.setNavigation(data['menus']);
          Configuration.setMagicBarItems(data['magicBarItems']);
          Configuration.setApplicationsSwitcher(data['applicationSwitcher']);
          Configuration.AddConfig('isSubscribedToNews', data['isSubscribedToNews']);
          Configuration.AddConfig('homePageName', data['homePageName']);
          Configuration.AddConfig('isSelfRegistered', data['isSelfRegistered']);
          Configuration.AddConfig('xatBotLicenseType', data['xatBotLicenseType']);
          // MPL-E-74-03 / P7.10: a billing admin (true) gets the app switcher's Manage Subscriptions pill on Pro+.
          Configuration.AddConfig('xatBotCanBuyForTeam', data['xatBotCanBuyForTeam']);
          Configuration.AddConfig('IsInitializationComplete', data['IsInitializationComplete']);
          Configuration.AddConfig('preferredLayout', data['preferredLayout'] ?? 'app-platform-tool-app-layout');
          Configuration.AddConfig('preferredDensity', data['preferredDensity'] ?? 'comfortable');
          // GEN-E-141-11: no default — an absent theme must stay absent, which the layout service reads as System.
          Configuration.AddConfig('preferredTheme', data['preferredTheme']);
          // ITP-E-335-01: the profile's IANA time zone, '' when none is set.
          Configuration.AddConfig('timeZone', data['timeZone'] ?? '');
          projectStore.dispatch(new GetCompanyBraningProfileSuccessAction(data['companyBrandingProfile']));
          projectStore.dispatch(new GetUserProfileSuccessAction(data['userProfile']));
          socketClusterService.initializeScService();

          projectStore.dispatch(new CreateDefaultProjectAction(new CreateDefaultProjectCommand()));
          projectStore.dispatch(new GetToastsAction());
          if (this.demoConfig
            && this.demoConfig.isAutomatedDemo
            && this.demoConfig.workflowFileId
            && this.demoConfig.hasWorkflowExecutionFinished === false) {
            securityStore.dispatch(new RunDemoWorkflowAction(new PlatformCommand('RunDemoWorkflowCommand', 'SecurityApi')));
          }

          if(data['Intention']){
           // debugger;
            particleCacheService.saveItem('Intention', data['Intention']);

          }

          return data;
        } else {
          console.error('get environment failed.');
        }
      });
  }

  static Init(httpClient: HttpClient, securityStore: Store<SecurityState>,
    projectStore: Store<ProjectState>, cookieService: CookieService,
    socketClusterService: SocketClusterService, particleCacheService: ParticleCacheService, router: Router, env: any,
    demoProvisioningService: DemoProvisioningService) {
    const self = this;
    return () => {
      console.log(`get config:: before http.get call`);
      Configuration.setConfig(env);
      return new Promise((resolve, reject) => {
        self.doAuthentication(securityStore, cookieService, router);
        this.isAuthenticated$.subscribe(isAuth => {
          switch (isAuth) {
            case AuthorizationType.AUTHORIZED:
              const config = Configuration.getConfig('SecurityApi');
              httpClient.get(`${config}Demo/StartupCheck`)
                .toPromise()
                .then(data => {
                  if (data && data['SupportAccessSessionMagicLinkUrl']) {
                    window.location.href = data['SupportAccessSessionMagicLinkUrl'];
                    return Promise.resolve(undefined);
                  }
                  if (data && data['SupportAccessSession']) {
                    securityStore.dispatch(new SetSupportAccessSessionAction(data['SupportAccessSession']));
                  }
                  const demoHistory = data && data['DemoHistory'];
                  if (!demoHistory) {
                    return Promise.resolve(null);
                  }
                  this.demoConfig = demoHistory;
                  // Gate bootstrap on demo readiness so a refresh during
                  // restore doesn't hand the demo token to the rest of the
                  // app before the demo databases are usable.
                  return demoProvisioningService.ensureDemoReady(demoHistory).toPromise();
                })
                .then(readyDemo => {
                  if (readyDemo === undefined) {
                    return undefined;
                  }
                  if (readyDemo) {
                    const accessToken = readyDemo.accessToken ?? readyDemo['AccessToken'];
                    securityStore.dispatch(new SetTempAccessTokenAction(accessToken));
                    securityStore.dispatch(new SetDemoAction(readyDemo));
                  }
                  // Keep splash visible until resolveConfig + default project (or first
                  // navigation) — same as pre-demo-gate behavior. Splash hide is handled
                  // by loadProjectSuccess / NavigationEnd, not here.
                  return self.resolveConfig(httpClient, env, projectStore, socketClusterService, particleCacheService, securityStore, router);
                })
                .catch(() => self.resolveConfig(httpClient, env, projectStore, socketClusterService, particleCacheService, securityStore, router));
              break;
            case AuthorizationType.UNAUTHORIZED:
              window.location.href = Configuration.DashboardUrl;
              break;
          }
        });

        const isResolved = true;
        projectStore.pipe(select(getCurrentProject)).subscribe(currentProject => {
          if (currentProject) {
            resolve(() => { });
          }
        });
        /* this.currentProject$ = projectStore.pipe(select(getCurrentProject));
         zip(this.currentProject$, socketClusterService.connected$).
         subscribe(([currentProject, isSCConnected]) => {
           if (currentProject && isSCConnected) {
             resolve(); //remove dependency from SC masud vai granted
           }
         });*/
      });
    };
  }
}
